import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { Layer } from 'bernie-view';

import { useLocalization } from '@shared-ui/localization-context';

import { SearchResultsStore } from 'stores/search-results-store';

import { UitkDialog, UitkDialogContent, UitkDialogFooter, UitkDialogFooterButtonProps } from 'uitk-react-dialog';
import { UitkEmptyState, UitkEmptyStateActionArea } from 'uitk-react-empty-state';
import { UitkHeading, UitkParagraph, UitkText } from 'uitk-react-text';
import { UitkIcon } from 'uitk-react-icons';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkTertiaryButton } from 'uitk-react-button';

import { handleLinkActionFromErrorMessage, handleRetry } from 'utils/error-handling-utils';

import { LodgingPwaPropertySearch } from '__generated__/api';

export interface PackageErrorProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
}

interface ErrorResponseProps {
  errorResponse: LodgingPwaPropertySearch.errorResponse;
  onClickHandler: (action: any) => void;
}
interface DialogErrorResponseProps {
  dialogErrorResponse: LodgingPwaPropertySearch.dialogErrorResponse;
  onClickHandler: (action: any) => void;
}

export const PackageError: React.FC<PackageErrorProps> = inject(
  'searchResults',
  'analytics'
)(({ searchResults, analytics }) => {
  const errorResponse = searchResults?.errorResponse;
  const dialogErrorResponse = searchResults?.dialogErrorResponse;
  const history = useHistory();
  const location = useLocation();

  const onClickHandler = (action: unknown) => handleLinkActionFromErrorMessage(action, analytics, searchResults, history, location);
  const retryHandler = () => handleRetry(':errorOnSearch', 'MultiItem Retry Search', analytics, searchResults, history, location);
  if (dialogErrorResponse) {
    return <DialogErrorResponse dialogErrorResponse={dialogErrorResponse} onClickHandler={onClickHandler} />;
  } else if (errorResponse) {
    return <ErrorFromErrorResponse errorResponse={errorResponse} onClickHandler={onClickHandler} />;
  }

  return <GenericError retryHandler={retryHandler} />;
});

const ErrorFromErrorResponse: React.FC<ErrorResponseProps> = ({ errorResponse, onClickHandler }) => {
  const errorRecoveryButtons = errorResponse.errorRecoveryButtons?.length ? (
    <UitkEmptyStateActionArea>
      {errorResponse.errorRecoveryButtons.map((error, index) => (
        <UitkTertiaryButton key={error.primary ?? index} name="textonly" onClick={() => onClickHandler(error.action)} type="button">
          {error.primary}
        </UitkTertiaryButton>
      ))}
    </UitkEmptyStateActionArea>
  ) : null;

  const defaultButton = (
    <UitkEmptyStateActionArea>
      <UitkTertiaryButton name="textonly" onClick={() => onClickHandler(errorResponse.button.action)} type="button">
        {errorResponse.button.primary}
      </UitkTertiaryButton>
    </UitkEmptyStateActionArea>
  );

  return (
    <UitkSpacing padding={{ blockstart: 'three' }}>
      <div>
        <UitkEmptyState data-stid={'multi-item-error-actions'}>
          <UitkIcon name={'search'} />
          <UitkHeading tag="h2">{errorResponse.header}</UitkHeading>
          <UitkText size={300} theme="default">
            {errorResponse.body}
          </UitkText>
          {errorRecoveryButtons}
          {defaultButton}
        </UitkEmptyState>
      </div>
    </UitkSpacing>
  );
};

interface GenericErrorProps {
  retryHandler: () => void;
}
const GenericError: React.FC<GenericErrorProps> = ({ retryHandler }) => {
  const { formatText } = useLocalization();

  return (
    <UitkSpacing padding={{ blockstart: 'three' }}>
      <div>
        <UitkEmptyState data-stid="multi-item-generic-error">
          <UitkIcon name={'search'} />
          <UitkHeading tag="h2">{formatText('multiItem.searchResults.error.text')}</UitkHeading>
          <UitkText size={300} theme="default">
            {formatText('multiItem.searchResults.tryAgain.text')}
          </UitkText>
          <UitkEmptyStateActionArea>
            <UitkTertiaryButton name="textonly" onClick={retryHandler} type="button">
              {formatText('multiItem.searchResults.retryButton.label')}
            </UitkTertiaryButton>
          </UitkEmptyStateActionArea>
        </UitkEmptyState>
      </div>
    </UitkSpacing>
  );
};

const DialogErrorResponse: React.FC<DialogErrorResponseProps> = ({ dialogErrorResponse, onClickHandler }) => {
  const {
    body,
    button: { action, primary },
  } = dialogErrorResponse;

  const buttonOne: UitkDialogFooterButtonProps = {
    onClick: () => onClickHandler(action),
    text: primary ?? '',
    type: 'tertiary',
  };

  return (
    <Layer id={'dialog-Error-Response-Layer'}>
      <UitkDialog dialogShow returnFocusOnClose={true}>
        <UitkDialogContent>
          <UitkParagraph size={2}>{body}</UitkParagraph>
          <UitkDialogFooter buttonOne={buttonOne} />
        </UitkDialogContent>
      </UitkDialog>
    </Layer>
  );
};
